import { useState } from "react";
import { handleFileUpload as handleFileUploadService } from "../services/fileService";
import { useNotification } from "../context/NotificationContext";

/**
 * useFileUpload - Custom hook for handling file uploads
 * @param {Function} onFileUploaded - Callback when file is successfully uploaded (receives fileData, fileType)
 * @returns {Object} { fileUploading, handleFileUpload }
 */
export function useFileUpload(onFileUploaded = null) {
  const { showError, showSuccess } = useNotification();
  const [fileUploading, setFileUploading] = useState({});

  const handleFileUpload = async (fileOrFiles, fileType) => {
    console.log(
      `[useFileUpload] Starting upload for type: ${fileType}`,
      fileOrFiles,
    );
    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    if (!files.length || !files[0])
      return { success: false, error: "No file provided" };

    setFileUploading((prev) => ({ ...prev, [fileType]: true }));

    const results = [];
    let lastError = null;

    try {
      for (const file of files) {
        try {
          console.log(`[useFileUpload] Uploading file: ${file.name}`);
          const fileData = await handleFileUploadService(file, fileType);
          console.log(`[useFileUpload] Upload success: ${file.name}`);

          if (onFileUploaded) {
            onFileUploaded(fileData, fileType);
          }
          results.push(fileData);
        } catch (error) {
          console.error(
            `[useFileUpload] Upload error for ${file.name}:`,
            error,
          );
          lastError = error;
          if (error.message === "File size exceeds 10MB limit") {
            showError("File size exceeds 10MB limit", "File Too Large");
          } else if (error.message.includes("Unsupported file type")) {
            showError(
              "Unsupported file type. Please upload PDF, DOCX, DOC, or TXT files.",
              "Invalid File Type",
            );
          } else if (error.message === "File too large") {
            showError(
              "File too large. Please try a smaller file.",
              "Upload Error",
            );
          } else if (error.response?.status === 403) {
            const errorMsg = error.response?.data?.message || "";
            if (
              errorMsg.includes("NO_SUBSCRIPTION") ||
              errorMsg.includes("subscription")
            ) {
              showError(
                "You must have a trial or active subscription to upload files.",
                "Subscription Required",
              );
            } else if (errorMsg.includes("TRIAL_UPLOAD_LIMIT_REACHED")) {
              showError(
                "You have reached your total 10 file upload limit for the trial.",
                "Trial Limit Reached",
              );
            } else {
              showError("Permission denied uploading file.", "Access Denied");
            }
          } else {
            showError("Error uploading file: " + error.message, "Upload Error");
          }
        }
      }

      if (results.length > 0) {
        showSuccess(
          `Uploaded ${results.length} file${results.length > 1 ? "s" : ""} successfully`,
          "Upload Complete",
        );
        // If single file input, return single data for backward compatibility
        if (!Array.isArray(fileOrFiles)) {
          return { success: true, fileData: results[0] };
        }
        return { success: true, fileData: results };
      }

      return { success: false, error: lastError };
    } finally {
      console.log(
        `[useFileUpload] Upload process finished for ${fileType}, resetting state.`,
      );
      setFileUploading((prev) => ({ ...prev, [fileType]: false }));
    }
  };

  return {
    fileUploading,
    handleFileUpload,
  };
}
